/**
 * Bridge Session Layout
 * Main container that wraps ExoPanel layout with Bridge Session-specific sidebar.
 * Reuses ChatOperationalLayout structure but replaces left sidebar with progress tracker.
 * @feature BRIDGE_SESSION_FLOW - Guided session layout: domains → entropy → Bridge Card → Control Bridge
 * @feature ISSUE-165 - Platform validation banner when platform not ready (backend error descriptions)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import { LeftProgressSidebar } from './LeftProgressSidebar';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import { useUser } from '../../contexts/UserContext';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import { platformValidationService, type PlatformValidationResult } from '../../services/platformValidationService';
import {
  companyModelReadinessService,
  type CompanyModelReadiness,
} from '../../services/companyModelReadinessService';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { DomainBuilder } from './DomainBuilder';
import { EntropyHeatMap } from './EntropyHeatMap';
import { BridgeCardLoader } from './BridgeCardLoader';
import { QuestionFlow } from './QuestionFlow';
import { FrictionPriorities } from './FrictionPriorities';
import { DefineConditionsAndPlanks } from './DefineConditionsAndPlanks';
import { ProductDomainBridgeLayout } from './ProductDomainBridgeLayout';
import { BridgeSessionIntentmlFacilitatorPanel } from './BridgeSessionIntentmlFacilitatorPanel';
import type { BridgeSessionStep } from '../../types/bridgeSession';
import styles from './BridgeSessionLayout.module.css';

interface BridgeSessionLayoutProps {
  sessionId?: string;
  sessionType?: 'business' | 'product';
  onStepChange?: (step: string) => void;
  onComplete?: () => void;
}

export const BridgeSessionLayout: React.FC<BridgeSessionLayoutProps> = ({
  sessionId: propSessionId,
  sessionType: propSessionType = 'business',
  onStepChange,
  onComplete,
}) => {
  const { sessionId: paramSessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = propSessionId || paramSessionId || '';
  const sessionType = propSessionType;
  const stepParam = searchParams.get('step') as BridgeSessionStep | null;
  // Keep URL step across reload; include entropy + domain so session init does not jump back to Domain Builder.
  const preserveStep =
    stepParam &&
    [
      'questions',
      'domain_builder',
      'entropy_heat_map',
      'friction_priorities',
      'define_conditions',
      'bridge_card',
      'control_bridge',
    ].includes(stepParam)
      ? stepParam
      : undefined;

  const {
    sessionState,
    currentStep,
    activeObjectiveId,
    initializeSession,
    setSessionState,
    navigateToStep,
    setActiveObjective,
  } = useBridgeSession();

  const { selectedCompany } = useUser();
  const [platformValidation, setPlatformValidation] = useState<PlatformValidationResult | null>(null);
  const [modelReadiness, setModelReadiness] = useState<CompanyModelReadiness | null>(null);

  // Platform validation (ISSUE-165) + model readiness (ISSUE-268)
  useEffect(() => {
    if (!sessionId || isLocalBridgeApiEnabled() || !selectedCompany?.id) return;

    const companyId = selectedCompany.id;

    const validatePlatform = async () => {
      try {
        const platform = await platformValidationService.validate(companyId);
        setPlatformValidation(platform);
      } catch {
        setPlatformValidation(null);
      }
    };

    const assessReadiness = async () => {
      try {
        const readiness = await companyModelReadinessService.assess(companyId);
        setModelReadiness(readiness);
      } catch {
        setModelReadiness(null);
      }
    };

    void validatePlatform();
    void assessReadiness();
  }, [sessionId, selectedCompany?.id]);

  // Load session state on mount
  useEffect(() => {
    if (!sessionId) return;

    const loadSessionState = async () => {
      try {
        const state = await bridgeSessionService.getSessionState(sessionId, sessionType);
        initializeSession(sessionId, state, preserveStep);
      } catch (error) {
        console.error('Failed to load Bridge Session state:', error);
        // No mock fallback: bridge session data must come from the session itself
      }
    };

    loadSessionState();
  }, [sessionId, sessionType, initializeSession, preserveStep]);

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  const renderMainContent = () => {
    switch (currentStep) {
      case 'questions':
        return <QuestionFlow />;
      case 'domain_builder':
        return <DomainBuilder />;
      case 'entropy_heat_map':
        return <EntropyHeatMap />;
      case 'friction_priorities':
        return <FrictionPriorities />;
      case 'define_conditions':
        return <DefineConditionsAndPlanks />;
      case 'bridge_card': {
        const objectiveId = sessionState?.bridgeCard.objective_artifact_item_id || activeObjectiveId || sessionId;
        if (objectiveId) {
          return <BridgeCardLoader objectiveArtifactItemId={objectiveId} />;
        }
        return (
          <div className={styles.emptyState}>
            <p>No objective selected. Please select an entropy area first.</p>
          </div>
        );
      }
      case 'control_bridge':
        window.location.href = sessionId ? `/dashboard?sessionId=${sessionId}` : '/dashboard';
        return null;
      default:
        return <DomainBuilder />;
    }
  };

  if (sessionType === 'product') {
    return <ProductDomainBridgeLayout />;
  }

  return (
    <div className={styles.bridgeSessionLayout}>
      {/* Left Progress Sidebar */}
      <aside className={styles.sidebar}>
        <LeftProgressSidebar />
      </aside>

      {/* Main Content Area - Reuse ChatOperationalLayout structure */}
      <main className={styles.mainContent}>
        {modelReadiness && !modelReadiness.model_ready && (
          <Alert
            type="warning"
            showIcon
            message="Company model not ready for Bridge Session"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  Bridge Session needs a provisioned workspace slug and imported company-model items in
                  Postgres (platform-init seed items alone are not enough). Billing or skeleton intake alone
                  is not enough.
                </p>
                <ul className={styles.validationErrorList}>
                  {modelReadiness.blockers.map((b) => (
                    <li key={b}>{b.replace(/_/g, ' ')}</li>
                  ))}
                </ul>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  Company-model items: {modelReadiness.checks.company_model_item_count ?? '—'}; total items:{' '}
                  {modelReadiness.checks.artifact_item_count}; domains:{' '}
                  {modelReadiness.checks.domain_artifact_count}; slug:{' '}
                  {modelReadiness.checks.kryspace_slug || 'missing'}.
                </p>
              </div>
            }
            className={styles.validationBanner}
          />
        )}
        {platformValidation && platformValidation.validation_status !== 'valid' && (
          <Alert
            type="warning"
            showIcon
            message="Platform not ready for Bridge Session"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  Bridge Session create requires full ISSUE-165 validation (Platform + subscription + Feature
                  lifecycles). That is stricter than Control Bridge&apos;s short-term gate policy. Model import
                  alone does not clear this banner.
                </p>
                {platformValidation.errors.length > 0 ? (
                  <ul className={styles.validationErrorList}>
                    {platformValidation.errors.map((e, i) => (
                      <li key={`${e.artifact_type}-${e.artifact_id ?? 'n'}-${i}`}>{e.error}</li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ marginBottom: 0 }}>
                    Platform artifacts are not fully initialized. Please contact support.
                  </p>
                )}
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  validation_status: {platformValidation.validation_status}
                  {modelReadiness?.checks.platform_gate_policy
                    ? `; Control Bridge policy: ${modelReadiness.checks.platform_gate_policy}`
                    : ''}
                  {modelReadiness?.checks.platform_gates_passed != null
                    ? `; CB gates_passed: ${String(modelReadiness.checks.platform_gates_passed)}`
                    : ''}
                </p>
              </div>
            }
            className={styles.validationBanner}
          />
        )}
        <div
          className={`${styles.contentWrapper} ${
            currentStep === 'domain_builder' ? styles.contentWrapperDomainBuilder : ''
          }`}
          style={currentStep === 'domain_builder' ? undefined : { overflowY: 'auto', padding: 24 }}
        >
          {sessionId && (
            <BridgeSessionIntentmlFacilitatorPanel sessionId={sessionId} companyId={selectedCompany?.id} />
          )}
          {renderMainContent()}
        </div>
      </main>
    </div>
  );
};
